import { useState } from "react";
import { Button, Modal, TextInput, useToast } from "@a2z/ui";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useUpdatePasswordMutation } from "@/modules/profile/services/useUpdatePasswordMutation";

const ps = keys.seller.profileSettings;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  storeSlug: string;
};

type Strength = { score: number; labelKey: string | null; color: string };

function pwdStrength(pwd: string): Strength {
  if (!pwd)
    return { score: 0, labelKey: null, color: "var(--border-light)" };
  let s = 0;
  if (pwd.length >= 8) s++;
  if (pwd.length >= 12) s++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
  if (/\d/.test(pwd) && /[^a-zA-Z0-9]/.test(pwd)) s++;
  const map = [
    { labelKey: ps.pwTooShort, color: "var(--error-500)" },
    { labelKey: ps.pwWeak, color: "var(--accent-500)" },
    { labelKey: ps.pwFair, color: "var(--accent-500)" },
    { labelKey: ps.pwGood, color: "#0284c7" },
    { labelKey: ps.pwStrong, color: "var(--success-500)" },
  ];
  return { score: s, ...map[s] };
}

export function PasswordModal({ isOpen, onClose, storeSlug }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: updatePassword, isPending } =
    useUpdatePasswordMutation();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const strength = pwdStrength(next);

  const valid =
    Boolean(current) &&
    Boolean(next) &&
    next === confirm &&
    strength.score >= 2;
  const canSave = valid && !isPending;

  const reset = (): void => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShowPwd(false);
  };

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    try {
      await updatePassword({
        storeSlug,
        currentPassword: current,
        nextPassword: next,
      });
      reset();
      toast.success(t(ps.passwordUpdated));
      onClose();
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    }
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={t(ps.password)}
      subtitle={t(ps.passwordDesc)}
      footerActions={
        <>
          <Button variant="outline" size="md" onClick={handleClose}>
            {t(keys.common.cancel)}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {isPending ? t(ps.updating) : t(ps.updatePassword)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t(ps.currentPassword)}>
          <TextInput
            name="currentPassword"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="••••••••"
            autoFocus
          />
        </Field>

        <Field label={t(ps.newPassword)}>
          <TextInput
            name="newPassword"
            type={showPwd ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            suffixSpace={64}
            suffix={
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="cursor-pointer border-none bg-transparent px-2 py-1 text-[11px] font-semibold text-[color:var(--text-muted)]"
                style={{ pointerEvents: "auto" }}
              >
                {showPwd ? t(ps.hide) : t(ps.show)}
              </button>
            }
          />
          <div className="mt-2 flex gap-[3px]">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-1 flex-1 rounded-[2px] transition-colors"
                style={{
                  background:
                    i < strength.score
                      ? strength.color
                      : "var(--border-light)",
                }}
              />
            ))}
          </div>
          <div
            className="mt-1 text-[11px] font-medium"
            style={{
              color: next ? strength.color : "var(--text-muted)",
            }}
          >
            {next
              ? strength.labelKey
                ? t(strength.labelKey)
                : ""
              : t(ps.pwHint)}
          </div>
        </Field>

        <Field label={t(ps.confirmPassword)}>
          <TextInput
            name="confirmPassword"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {confirm && next ? (
            <div
              className="mt-1 text-[11px] font-medium"
              style={{
                color:
                  confirm === next
                    ? "var(--success-500)"
                    : "var(--error-500)",
              }}
            >
              {confirm === next
                ? `✓ ${t(ps.passwordsMatch)}`
                : t(ps.passwordsNoMatch)}
            </div>
          ) : null}
        </Field>
      </div>
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium text-[color:var(--label-ink)]">
        {label}
      </span>
      {children}
    </label>
  );
}
